import { useCallback, useEffect } from 'react';

import type { GasPayload } from '@atlaskit/analytics-gas-types';

import { PRODUCTS_WITH_INLINE_FILTERS } from '../../../../../common/constants/quick-find';
import {
	SearchExperience,
	type SearchStoreExperience,
} from '../../../../../common/constants/search';
import type { FilterChangeType } from '../../../../../common/ui/filters/product-filters/types';
import {
	type FilterChangeLocation,
	onFilterClear,
	onFilterDropdown,
	onFilterSelection,
	onSingleFilterClear,
} from '../../../../../common/utils/filters/events';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { useSelectedQueryFilters } from '../../../../../controllers/store/quick-find';
import { useAnyFilterSelected, useSearchActions } from '../../../../../controllers/store/search';

export type OnFilterChangeCallback = (
	filterId: string,
	changes: FilterChangeType | FilterChangeType[],
	location: FilterChangeLocation,
) => void;

/**
 * This is a kind of a band-aid solution to deselect the product filter
 * in Quick Find. This is because when users interact with an inline product
 * filter (e.g. space filter), the primary product is set as the product filter.
 * Since there is no UI component to deselect the product filter, we end up in
 * a state where the product filter is selected without any UI indication. This
 * is problematic as the "Recent Activities" section in Quick Find becomes
 * unavailable whenever any filter is enabled.
 */
export const useUnselectProductFilter: (options?: { isEnabled?: boolean }) => void = ({
	isEnabled = true,
}: { isEnabled?: boolean } = {}): void => {
	const [primaryProduct] = usePrimaryProduct();
	const { unselectProductFilter } = useSearchActions();
	const [isAnyFilterSelected] = useAnyFilterSelected({ includeSelectedProduct: false });
	const selectedQueryFilters = useSelectedQueryFilters();

	useEffect(() => {
		if (!PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct) || !isEnabled) {
			return;
		}

		// TODO: Make this smarter. Ideally, we should only unselect the product filter,
		// when that product filter is enabled and those product filters are empty. However
		// the isEmpty within useSelectedProduct is very very hard coded at the moment.
		if (!isAnyFilterSelected && selectedQueryFilters.length === 0) {
			unselectProductFilter();
		}
	}, [
		isAnyFilterSelected,
		primaryProduct,
		unselectProductFilter,
		selectedQueryFilters.length,
		isEnabled,
	]);
};

export const useFilterAnalytics: (
	fireAnalyticsEvent: (payload: GasPayload) => void,
	commonAttributes: Record<string, unknown>,
	experience: SearchStoreExperience,
) => {
	onFilterChange: OnFilterChangeCallback;
	onDropdownOpenChange: (isOpen: boolean) => void;
	onFilterClearAction: (location: FilterChangeLocation) => void;
	onSingleFilterClearAction: ({ filterId }: { filterId: string }) => void;
} = (
	fireAnalyticsEvent: (payload: GasPayload) => void,
	commonAttributes: Record<string, unknown>,
	experience: SearchStoreExperience,
) => {
	const onFilterChange: OnFilterChangeCallback = useCallback(
		(filterId, changes, location) => {
			// Omitting these attrtibutes as there is a race condition here since the filter attributes
			// have not yet been applied in the store so it will always be one update behind.
			const {
				productSelected: _productSelected,
				filters: _filters,
				filterSelections,
				...restAttributes
			} = commonAttributes;
			const change = (Array.isArray(changes) ? changes : [changes])[0];

			if (!change) {
				return; // This should never be executed
			}

			fireAnalyticsEvent(
				onFilterSelection({
					action: change.isSelected ? 'unselected' : 'selected',
					actionSubjectId:
						experience === SearchExperience.QUICK_FIND ? 'searchDialogFilter' : 'searchPageFilter',
					location,
					attributes: {
						...restAttributes,
						filterId,
						location,
					},
				}),
			);
		},
		[commonAttributes, experience, fireAnalyticsEvent],
	);

	const onDropdownOpenChange = useCallback(
		(isOpen: boolean): void => {
			fireAnalyticsEvent(
				onFilterDropdown({
					action: isOpen ? 'open' : 'close',
					actionSubjectId:
						experience === SearchExperience.QUICK_FIND
							? 'searchDialogDropdown'
							: 'searchPageDropdown',
					attributes: {
						...commonAttributes,
					},
				}),
			);
		},
		[commonAttributes, experience, fireAnalyticsEvent],
	);

	const onFilterClearAction = useCallback(
		(location: FilterChangeLocation): void => {
			fireAnalyticsEvent(
				onFilterClear({
					location,
					actionSubjectId:
						experience === SearchExperience.QUICK_FIND ? 'searchDialogFilter' : 'searchPageFilter',
					attributes: {
						...commonAttributes,
					},
				}),
			);
		},
		[commonAttributes, experience, fireAnalyticsEvent],
	);

	const onSingleFilterClearAction = useCallback(
		({ filterId }: { filterId: string }) => {
			const filters = [filterId];

			const filterSelections = Array.isArray(commonAttributes?.filterSelections)
				? commonAttributes?.filterSelections.filter((s) => s?.filterId === filterId)
				: [];

			fireAnalyticsEvent(
				onSingleFilterClear({
					location: filterId,
					actionSubjectId:
						experience === SearchExperience.QUICK_FIND ? 'searchDialogFilter' : 'searchPageFilter',
					attributes: {
						...commonAttributes,
						filters,
						filterSelections,
					},
				}),
			);
		},
		[commonAttributes, experience, fireAnalyticsEvent],
	);

	return { onFilterChange, onDropdownOpenChange, onFilterClearAction, onSingleFilterClearAction };
};
